import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Mail, Send, Bell, RefreshCw, CheckCircle2 } from "lucide-react";
import { api, priceFmt } from "../lib/api";

export default function NewsletterAdminTab() {
  const qc = useQueryClient();
  const [testTo, setTestTo] = useState("");
  const [feedback, setFeedback] = useState({ kind: "", text: "" });

  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ["newsletter-stats"],
    queryFn: () => api.get("/admin/newsletter/stats").then((r) => r.data),
    staleTime: 30_000,
  });

  const { data: subsData } = useQuery({
    queryKey: ["newsletter-subscribers"],
    queryFn: () =>
      api.get("/admin/newsletter/subscribers?limit=50").then((r) => r.data),
    staleTime: 30_000,
  });

  const { data: alertsData } = useQuery({
    queryKey: ["price-alerts-list"],
    queryFn: () =>
      api.get("/admin/price-alerts?limit=100").then((r) => r.data),
    staleTime: 30_000,
  });

  const sendTest = useMutation({
    mutationFn: () =>
      api.post("/admin/newsletter/send-test", { to: testTo }).then((r) => r.data),
    onSuccess: (d) =>
      setFeedback({
        kind: "ok",
        text: `Email test inviata a ${testTo} (id: ${d.email_id?.slice(0, 8)}…, ${d.deals_included} offerte).`,
      }),
    onError: (err) =>
      setFeedback({
        kind: "err",
        text: err?.response?.data?.detail || "Errore invio email di test.",
      }),
  });

  const sendWelcomeTest = useMutation({
    mutationFn: (stage) =>
      api
        .post("/admin/newsletter/send-welcome-test", { to: testTo, stage })
        .then((r) => r.data),
    onSuccess: (d) =>
      setFeedback({
        kind: "ok",
        text: `Welcome ${d.stage} inviata a ${testTo} (id: ${d.email_id?.slice(0, 8)}…).`,
      }),
    onError: (err) =>
      setFeedback({
        kind: "err",
        text: err?.response?.data?.detail || "Errore invio welcome test.",
      }),
  });

  const runWelcomeSeries = useMutation({
    mutationFn: () =>
      api.post("/admin/newsletter/welcome-series-check-now").then((r) => r.data),
    onSuccess: (d) =>
      setFeedback({
        kind: "ok",
        text: `Welcome-series: inviate ${d.sent_day2 || 0} day-2 e ${d.sent_day7 || 0} day-7 (fallite ${d.failed || 0}).`,
      }),
    onError: (err) =>
      setFeedback({
        kind: "err",
        text: err?.response?.data?.detail || "Errore welcome-series.",
      }),
  });

  const sendArticleTest = useMutation({
    mutationFn: () =>
      api
        .post("/admin/newsletter/send-article-test", { to: testTo })
        .then((r) => r.data),
    onSuccess: (d) =>
      setFeedback({
        kind: "ok",
        text: `Test articolo "${d.article_slug}" inviato a ${testTo} (id: ${d.email_id?.slice(0, 8)}…).`,
      }),
    onError: (err) =>
      setFeedback({
        kind: "err",
        text: err?.response?.data?.detail || "Errore invio articolo test.",
      }),
  });

  const sendArticleNow = useMutation({
    mutationFn: () =>
      api.post("/admin/newsletter/send-article-now").then((r) => r.data),
    onSuccess: (d) => {
      setFeedback({
        kind: "ok",
        text: `Articolo "${d.article_slug}" inviato a ${d.sent} iscritti (skipped ${d.skipped}, failed ${d.failed}).`,
      });
      refetchStats();
    },
    onError: (err) =>
      setFeedback({
        kind: "err",
        text: err?.response?.data?.detail || "Errore invio articolo.",
      }),
  });

  const sendNow = useMutation({
    mutationFn: () =>
      api.post("/admin/newsletter/send-now").then((r) => r.data),
    onSuccess: (d) => {
      setFeedback({
        kind: "ok",
        text: `Newsletter inviata a ${d.sent} iscritti (skipped ${d.skipped}, failed ${d.failed}) — campaign ${d.campaign}.`,
      });
      refetchStats();
      qc.invalidateQueries({ queryKey: ["newsletter-subscribers"] });
    },
    onError: (err) =>
      setFeedback({
        kind: "err",
        text: err?.response?.data?.detail || "Errore invio newsletter.",
      }),
  });

  const checkAlerts = useMutation({
    mutationFn: () =>
      api.post("/admin/price-alerts/check-now").then((r) => r.data),
    onSuccess: (d) => {
      setFeedback({
        kind: "ok",
        text: `Alert prezzo controllati: ${d.checked} totali, ${d.fired} inviati, ${d.failed} falliti.`,
      });
      qc.invalidateQueries({ queryKey: ["price-alerts-list"] });
    },
    onError: (err) =>
      setFeedback({
        kind: "err",
        text: err?.response?.data?.detail || "Errore check alert.",
      }),
  });

  return (
    <div className="space-y-6" data-testid="newsletter-admin-tab">
      <div>
        <h1 className="font-display font-black text-2xl text-white">
          Newsletter & Alert Prezzo
        </h1>
        <p className="text-zinc-500 text-sm">
          Gestione iscritti, invio manuale della newsletter settimanale (venerdì
          mattina automatico) e monitoraggio degli avvisi di prezzo.
        </p>
      </div>

      {/* stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Iscritti attivi" value={stats?.subscribers_active ?? "…"} accent="#FBD51C" />
        <StatCard label="Disiscritti" value={stats?.subscribers_unsub ?? "…"} accent="#71717a" />
        <StatCard label="Alert attivi" value={stats?.alerts_active ?? "…"} accent="#229ED9" />
        <StatCard label="Alert scattati" value={stats?.alerts_triggered ?? "…"} accent="#22c55e" />
      </div>

      <div className="text-xs text-zinc-500 flex items-center gap-2">
        <span
          className={`inline-block w-2 h-2 rounded-full ${stats?.resend_configured ? "bg-green-500" : "bg-rose-500"}`}
        />
        Resend {stats?.resend_configured ? "configurato" : "NON configurato"} · sender:{" "}
        <span className="text-zinc-300 font-mono">{stats?.sender || "—"}</span>
      </div>

      {feedback.text && (
        <div
          className={`text-sm rounded-lg p-3 ${feedback.kind === "ok" ? "bg-green-500/10 text-green-300 border border-green-500/30" : "bg-rose-500/10 text-rose-300 border border-rose-500/30"}`}
          data-testid="newsletter-admin-feedback"
        >
          {feedback.text}
        </div>
      )}

      {/* actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* send test */}
        <div className="rounded-2xl bg-zinc-900/60 border border-zinc-800 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Mail size={16} className="text-[#FBD51C]" />
            <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C]">
              Invio di test
            </div>
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Manda l&apos;anteprima della newsletter a un solo indirizzo (non tocca gli
            iscritti).
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="test@email.com"
              data-testid="newsletter-test-email-input"
              className="flex-1 bg-black/40 border border-zinc-800 focus:border-[#FBD51C] outline-none rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600"
            />
            <button
              onClick={() => testTo && sendTest.mutate()}
              disabled={!testTo || sendTest.isPending}
              data-testid="newsletter-send-test-btn"
              className="bg-[#FBD51C] hover:bg-[#F5C518] disabled:bg-zinc-800 disabled:text-zinc-500 text-black font-black text-xs uppercase tracking-wider px-4 py-2 rounded-lg"
            >
              {sendTest.isPending ? "…" : "Invia test"}
            </button>
          </div>
        </div>

        {/* send now */}
        <div className="rounded-2xl bg-zinc-900/60 border border-zinc-800 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Send size={16} className="text-[#229ED9]" />
            <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#229ED9]">
              Invio manuale a tutti
            </div>
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Invia subito la newsletter settimanale a tutti gli iscritti attivi.
            Idempotente: già inviate a questa campagna verranno saltate.
          </p>
          <button
            onClick={() => {
              if (window.confirm("Confermi l'invio della newsletter a tutti gli iscritti attivi?")) {
                sendNow.mutate();
              }
            }}
            disabled={sendNow.isPending || !stats?.resend_configured}
            data-testid="newsletter-send-now-btn"
            className="bg-[#229ED9] hover:bg-[#1c8acb] disabled:bg-zinc-800 disabled:text-zinc-500 text-white font-black text-xs uppercase tracking-wider px-4 py-2 rounded-lg"
          >
            {sendNow.isPending ? "Invio in corso…" : "Invia adesso"}
          </button>
        </div>
      </div>

      {/* Force alert check */}
      <div className="rounded-2xl bg-zinc-900/60 border border-zinc-800 p-5 flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Bell size={16} className="text-[#FBD51C]" />
            <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C]">
              Alert prezzo
            </div>
          </div>
          <p className="text-xs text-zinc-500 max-w-xl">
            Il controllo automatico gira ogni 30 minuti. Puoi forzare un check
            immediato di tutti gli alert attivi.
          </p>
        </div>
        <button
          onClick={() => checkAlerts.mutate()}
          disabled={checkAlerts.isPending}
          data-testid="alerts-check-now-btn"
          className="bg-[#FBD51C] hover:bg-[#F5C518] disabled:bg-zinc-800 disabled:text-zinc-500 text-black font-black text-xs uppercase tracking-wider px-4 py-2 rounded-lg inline-flex items-center gap-1.5 whitespace-nowrap"
        >
          <RefreshCw size={13} /> {checkAlerts.isPending ? "…" : "Controlla ora"}
        </button>
      </div>

      {/* Welcome series force check */}
      <div className="rounded-2xl bg-zinc-900/60 border border-zinc-800 p-5 flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Mail size={16} className="text-[#229ED9]" />
            <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#229ED9]">
              Welcome series
            </div>
          </div>
          <p className="text-xs text-zinc-500 max-w-xl">
            Email automatiche a nuovi iscritti: giorno 0 (benvenuto), giorno 2
            (guida rapida), giorno 7 (preview del venerdì). Il check
            automatico gira ogni ora.
          </p>
        </div>
        <button
          onClick={() => runWelcomeSeries.mutate()}
          disabled={runWelcomeSeries.isPending}
          data-testid="welcome-series-check-now-btn"
          className="bg-[#229ED9] hover:bg-[#1c8acb] disabled:bg-zinc-800 disabled:text-zinc-500 text-white font-black text-xs uppercase tracking-wider px-4 py-2 rounded-lg inline-flex items-center gap-1.5 whitespace-nowrap"
        >
          <RefreshCw size={13} /> {runWelcomeSeries.isPending ? "…" : "Esegui adesso"}
        </button>
      </div>

      {/* Tuesday article newsletter */}
      <div className="rounded-2xl bg-zinc-900/60 border border-zinc-800 p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail size={16} className="text-orange-400" />
              <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-orange-400">
                Articolo del martedì
              </div>
            </div>
            <p className="text-xs text-zinc-500 max-w-xl">
              Ogni <b>martedì mattina 08:00</b> gli iscritti ricevono l&apos;ultimo
              articolo pubblicato dal blog. Idempotente: nessuno riceve lo
              stesso articolo due volte.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => testTo && sendArticleTest.mutate()}
            disabled={!testTo || sendArticleTest.isPending}
            data-testid="article-send-test-btn"
            className="bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-900 disabled:text-zinc-600 text-zinc-200 font-bold text-[11px] uppercase tracking-wider px-3 py-2 rounded-lg border border-zinc-700"
          >
            Test articolo → {testTo || "email test"}
          </button>
          <button
            onClick={() => {
              if (window.confirm("Confermi invio dell'ultimo articolo pubblicato a tutti gli iscritti attivi?")) {
                sendArticleNow.mutate();
              }
            }}
            disabled={sendArticleNow.isPending}
            data-testid="article-send-now-btn"
            className="bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-800 disabled:text-zinc-500 text-black font-black text-xs uppercase tracking-wider px-4 py-2 rounded-lg"
          >
            {sendArticleNow.isPending ? "Invio…" : "Invia articolo adesso"}
          </button>
        </div>
      </div>

      {/* Subscribers list */}
      <div>
        <h2 className="font-display font-black text-lg text-white mb-3">
          Iscritti recenti ({subsData?.count || 0})
        </h2>
        <div className="rounded-2xl bg-zinc-900/40 border border-zinc-800 overflow-hidden">
          {subsData?.items?.length ? (
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60">
                <tr>
                  <th className="text-left px-4 py-2 text-zinc-500 font-mono-bdd text-[10px] uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-2 text-zinc-500 font-mono-bdd text-[10px] uppercase tracking-wider">Stato</th>
                  <th className="text-left px-4 py-2 text-zinc-500 font-mono-bdd text-[10px] uppercase tracking-wider">Fonte</th>
                  <th className="text-left px-4 py-2 text-zinc-500 font-mono-bdd text-[10px] uppercase tracking-wider">Iscritto il</th>
                </tr>
              </thead>
              <tbody>
                {subsData.items.map((s) => (
                  <tr key={s.email} className="border-t border-zinc-800/60">
                    <td className="px-4 py-2 text-white font-mono text-xs">{s.email}</td>
                    <td className="px-4 py-2">
                      {s.subscribed ? (
                        <span className="inline-flex items-center gap-1 text-green-400 text-xs">
                          <CheckCircle2 size={12} /> attivo
                        </span>
                      ) : (
                        <span className="text-zinc-500 text-xs">disiscritto</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-zinc-400 text-xs">{s.source || "—"}</td>
                    <td className="px-4 py-2 text-zinc-500 text-xs font-mono">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString("it-IT") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-6 text-zinc-500 text-sm">Nessun iscritto ancora.</div>
          )}
        </div>
      </div>

      {/* Alerts list */}
      <div>
        <h2 className="font-display font-black text-lg text-white mb-3">
          Alert prezzo ({alertsData?.count || 0})
        </h2>
        <div className="rounded-2xl bg-zinc-900/40 border border-zinc-800 overflow-hidden">
          {alertsData?.items?.length ? (
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60">
                <tr>
                  <th className="text-left px-4 py-2 text-zinc-500 font-mono-bdd text-[10px] uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-2 text-zinc-500 font-mono-bdd text-[10px] uppercase tracking-wider">Deal</th>
                  <th className="text-left px-4 py-2 text-zinc-500 font-mono-bdd text-[10px] uppercase tracking-wider">Target</th>
                  <th className="text-left px-4 py-2 text-zinc-500 font-mono-bdd text-[10px] uppercase tracking-wider">Stato</th>
                </tr>
              </thead>
              <tbody>
                {alertsData.items.map((a) => (
                  <tr key={a.id} className="border-t border-zinc-800/60">
                    <td className="px-4 py-2 text-white font-mono text-xs">{a.email}</td>
                    <td className="px-4 py-2 text-zinc-400 text-xs font-mono">
                      {a.deal_id?.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-2 text-[#FBD51C] font-bold">
                      {priceFmt(a.target_price)}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {a.active ? (
                        <span className="text-green-400">attivo</span>
                      ) : a.triggered_at ? (
                        <span className="text-[#229ED9]">inviato @ {priceFmt(a.trigger_price)}</span>
                      ) : (
                        <span className="text-zinc-500">cancellato</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-6 text-zinc-500 text-sm">Nessun alert prezzo ancora.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-zinc-900/60 border border-zinc-800 p-4">
      <div
        className="font-mono-bdd text-[10px] uppercase tracking-[0.25em] mb-1"
        style={{ color: accent }}
      >
        {label}
      </div>
      <div className="font-display font-black text-3xl text-white">{value}</div>
    </div>
  );
}
